if (!customElements.get('midi-product-content')) {
  customElements.define('midi-product-content', class extends HTMLElement {
    connectedCallback() {
      this.listeners?.abort();
      this.listeners = new AbortController();
      this.animations = new Map();
      this.targetOpen = new WeakMap();
      this.revealed = new Map();

      // Native grouping works without JavaScript; this element manages the
      // group when active so closing rows can finish their animation.
      this.accordions.forEach(details => details.removeAttribute('name'));

      this.addEventListener('click', event => {
        const summary = event.target.closest('summary');
        const details = summary?.parentElement;
        if (!details?.matches('.pdp-accordion') || details.closest('midi-product-content') !== this) return;
        event.preventDefault();
        this.setOpen(details, !(this.targetOpen.get(details) ?? details.open));
      }, { signal: this.listeners.signal });

      this.addEventListener('shopify:block:select', event => {
        const block = event.target.closest('[data-product-block]');
        if (!block || block.closest('midi-product-content') !== this) return;
        block.setAttribute('data-editor-selected', '');
        const details = block.querySelector('details');
        if (details) {
          if (!this.revealed.has(details)) this.revealed.set(details, this.targetOpen.get(details) ?? details.open);
          this.setOpen(details, true, false);
        }
      }, { signal: this.listeners.signal });
      this.addEventListener('shopify:block:deselect', event => {
        const block = event.target.closest('[data-product-block]');
        if (!block || block.closest('midi-product-content') !== this) return;
        block.removeAttribute('data-editor-selected');
        this.restore(block.querySelector('details'));
      }, { signal: this.listeners.signal });
    }

    get accordions() {
      return [...this.querySelectorAll('details.pdp-accordion')]
        .filter(details => details.closest('midi-product-content') === this);
    }

    get groupContents() {
      return [...document.querySelectorAll('midi-product-content[data-accordion-group]')]
        .filter(content => content.dataset.accordionGroup === this.dataset.accordionGroup);
    }

    setOpen(details, open, animate = true) {
      if (open) {
        this.groupContents.forEach(content => {
          content.accordions.forEach(other => {
            if (other !== details && (content.targetOpen.get(other) ?? other.open)) content.setOpen(other, false, animate);
          });
        });
      }

      const currentHeight = details.getBoundingClientRect().height;
      const previous = this.animations.get(details);
      if (previous) {
        previous.onfinish = null;
        previous.cancel();
        this.animations.delete(details);
      }

      this.targetOpen.set(details, open);
      if (!animate || !details.animate || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        details.open = open;
        details.style.removeProperty('overflow');
        return;
      }

      let endHeight;
      if (open) {
        details.open = true;
        endHeight = details.scrollHeight;
      } else {
        // Measure the closed summary while keeping the answer visible for exit motion.
        details.open = false;
        endHeight = details.getBoundingClientRect().height;
        details.open = true;
      }

      if (Math.abs(currentHeight - endHeight) < 1) {
        details.open = open;
        details.style.removeProperty('overflow');
        return;
      }

      details.style.overflow = 'hidden';
      const animation = details.animate(
        [{ height: `${currentHeight}px` }, { height: `${endHeight}px` }],
        { duration: 260, easing: 'ease-in-out' }
      );
      this.animations.set(details, animation);
      animation.onfinish = () => {
        details.open = open;
        details.style.removeProperty('overflow');
        this.animations.delete(details);
      };
    }

    restore(details) {
      if (!this.revealed?.has(details)) return;
      this.setOpen(details, this.revealed.get(details), false);
      this.revealed.delete(details);
    }

    disconnectedCallback() {
      this.listeners?.abort();
      this.animations?.forEach((animation, details) => {
        animation.onfinish = null;
        animation.cancel();
        details.open = this.targetOpen.get(details);
        details.style.removeProperty('overflow');
      });
      this.animations?.clear();
      this.revealed?.forEach((_, details) => this.restore(details));
      this.querySelectorAll('[data-editor-selected]').forEach(block => block.removeAttribute('data-editor-selected'));
    }
  });
}
